<script setup lang="ts">
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import { useI18n } from 'vue-i18n'
import { setLocale, SUPPORTED_LOCALES, LOCALE_NAMES, LOCALE_FLAG_KEY, type SupportedLocale } from '@/locales'
import { usePlayerFlagsStore } from '@/stores/playerFlagsStore'
import { ref, computed } from 'vue'

const { t, locale } = useI18n()
const playerFlagsStore = usePlayerFlagsStore()
const isChanging = ref(false)

const sortedLocales = computed(() =>
  [...SUPPORTED_LOCALES].sort((a, b) => LOCALE_NAMES[a].localeCompare(LOCALE_NAMES[b]))
)

const LOCALE_FLAGS: Record<SupportedLocale, string> = {
  'en-us': '🇺🇸',
  'en-gb': '🇬🇧',
  'es-es': '🇪🇸',
  'pt-br': '🇧🇷',
  'bg-bg': '🇧🇬',
  'pl-pl': '🇵🇱',
  'fr-fr': '🇫🇷',
  'it-it': '🇮🇹',
  'de-de': '🇩🇪',
  'ru-ru': '🇷🇺',
  'zh-cn': '🇨🇳',
  'tr-tr': '🇹🇷',
  'ko-kr': '🇰🇷',
  'cs-cz': '🇨🇿',
  'hu-hu': '🇭🇺',
  'ro-ro': '🇷🇴',
  'uk-ua': '🇺🇦',
  'nl-nl': '🇳🇱'
}

async function changeLanguage(event: Event) {
  const target = event.target as HTMLSelectElement
  const newLocale = target.value as SupportedLocale

  if (newLocale === locale.value) return

  isChanging.value = true
  try {
    await setLocale(newLocale)
    await playerFlagsStore.setFlag(LOCALE_FLAG_KEY, newLocale)
  } finally {
    isChanging.value = false
  }
}
</script>

<template>
  <StandardCard :title="t('settings.language.title')">
    <div class="flex justify-between items-center">
      <div>
        {{ t('settings.language.description') }}
      </div>
      <div class="flex items-center">
        <select
          :value="locale"
          :disabled="isChanging"
          class="bg-slate-700 text-white border border-slate-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50"
          @change="changeLanguage"
        >
          <option
            v-for="loc in sortedLocales"
            :key="loc"
            :value="loc"
          >
            {{ LOCALE_FLAGS[loc] }}&ensp;{{ LOCALE_NAMES[loc] }}
          </option>
        </select>
      </div>
    </div>
  </StandardCard>
</template>

<style scoped lang="scss">
select {
  cursor: pointer;
  min-width: 160px;
}
</style>
